// src/features/engine/tasks/osrboi/hooks/useOSRBOITask.js

import { useEffect, useState, useMemo } from "react";
import axios from "axios";

import { useProgramEngine } from "../../../context/ProgramEngineContext";
import { useTaskRuntime } from "../../../context/TaskRuntimeContext";
import { useSharedAssessments } from "../../../../../hooks/useSharedAssessments";
import { buildOSRBOIFeedback } from "../services/buildOSRBOIFeedback";

export default function useOSRBOITask() {

  const programEngine =
    useProgramEngine() || {};

  const {
    session,
    step,
    task,
    responses = [],
    apiUrl: runtimeApiUrl,
    token: runtimeToken,
    completeCurrentStep: runtimeCompleteCurrentStep,
    refetchProgramState: runtimeRefetchProgramState
  } = useTaskRuntime();

  const apiUrl =
    runtimeApiUrl || programEngine.apiUrl;

  const token =
    runtimeToken || programEngine.token;

  const completeCurrentStep =
    runtimeCompleteCurrentStep ||
    programEngine.completeCurrentStep;

  const refetchProgramState =
    runtimeRefetchProgramState ||
    programEngine.refetchProgramState ||
    (async () => undefined);

  const [samples, setSamples] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [allRatings, setAllRatings] = useState([]);

  const [showFeedback, setShowFeedback] = useState(false);
  const [currentRatings, setCurrentRatings] = useState(null);
  const [referenceValues, setReferenceValues] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  // ===============================================================
  // TRIALS
  // ===============================================================

  const trials = useMemo(() => {
    return session?.presentedTrials || [];
  }, [session]);

  // ===============================================================
  // RESUME LOGIC
  // ===============================================================

  useEffect(() => {

    if (!trials.length) return;
    if (!responses.length) return;

    const answeredTrialIds = responses.map(r =>
      r.trialId?.toString()
    );

    const nextIndex = trials.findIndex(
      t => !answeredTrialIds.includes(t.trialId.toString())
    );

    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    }

  }, [trials, responses]);

  const currentTrial = trials[currentIndex] || null;

  const currentSampleId =
    currentTrial?.stimuli?.[0]?.voiceSampleId || null;

  const currentSample = useMemo(() => {
    if (!currentSampleId) return null;

    return samples.find(
      s => s._id.toString() === currentSampleId.toString()
    ) || null;
  }, [samples, currentSampleId]);

  const { sharedAssessments } =
    useSharedAssessments(apiUrl, currentSampleId || null);

  // ===============================================================
  // LOAD SAMPLES
  // ===============================================================

  useEffect(() => {

    if (!trials.length) return;

    const ids = [
      ...new Set(
        trials
          .map(t => t?.stimuli?.[0]?.voiceSampleId?.toString())
          .filter(Boolean)
      )
    ];

    if (!ids.length) return;

    const fetchSamples = async () => {
      try {

        const res = await axios.get(
          `${apiUrl}/voice-samples`,
          { params: { ids: ids.join(",") } }
        );

        setSamples(res.data.data || []);

      } catch (err) {
        console.error("Samples load error:", err);
      }
    };

    fetchSamples();

  }, [trials, apiUrl]);

  // ===============================================================
  // FEEDBACK
  // ===============================================================

useEffect(() => {

  if (!sharedAssessments || !showFeedback) return;

  const feedback =
    buildOSRBOIFeedback(sharedAssessments, currentRatings);

  setReferenceValues(feedback);

}, [sharedAssessments, showFeedback, currentRatings]);

  // ===============================================================
  // SUBMIT LOCAL
  // ===============================================================

  const submitEvaluation = async (formData) => {

    if (!currentTrial) return;

    try {

      const formatted = {
        trialKey: currentTrial.trialKey,
        stimulusInstanceId: currentTrial.stimuli[0].stimulusInstanceId,
        response: {
          overallSeverity: formData.overallSeverity,
          roughness: formData.roughness,
          breathiness: formData.breathiness
        }
      };

      await axios.post(
        `${apiUrl}/evaluation/response`,
        {
          sessionId: session._id,
          trialId: currentTrial.trialId,
          trialKey: currentTrial.trialKey,
          stimulusInstanceId: formatted.stimulusInstanceId,
          responseType: "osrboi",
          response: formatted.response
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setAllRatings(prev => [...prev, formatted]);

      setCurrentRatings(formData);
      setShowFeedback(true);

    } catch (err) {
      console.error("Submit error:", err);
    }
  };

  // ===============================================================
  // NEXT
  // ===============================================================

  const next = async () => {

    const isLast =
      currentIndex + 1 >= trials.length;

    if (isLast) {
      await submitStep();
    } else {

      setShowFeedback(false);
      setCurrentRatings(null);
      setReferenceValues(null);
      setCurrentIndex(prev => prev + 1);

    }

  };

  // ===============================================================
  // SUBMIT STEP
  // ===============================================================

  const submitStep = async () => {

    try {

      setSubmitting(true);

      for (const r of allRatings) {

        const trial = trials.find(t => t.trialKey === r.trialKey);
        if (!trial) continue;

        await axios.post(
          `${apiUrl}/evaluation/response`,
          {
            sessionId: session._id,
            trialId: trial.trialId,
            trialKey: r.trialKey,
            stimulusInstanceId: r.stimulusInstanceId,
            responseType: "osrboi",
            response: r.response
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      if (completeCurrentStep) {
        await completeCurrentStep(session._id);
      }
      await refetchProgramState();

      setCurrentIndex(0);
      setAllRatings([]);
      setShowFeedback(false);
      setCurrentRatings(null);
      setReferenceValues(null);

    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }

  };

  // ===============================================================
  // PROGRESS
  // ===============================================================

  const totalSamples = trials.length;

  const progress =
    totalSamples > 0
      ? ((currentIndex + 1) / totalSamples) * 100
      : 0;

  const loading =
    !session || !task || !trials.length || samples.length === 0;

  // ===============================================================
  // RETURN
  // ===============================================================

  return {
    session, 
    step,
    task,
    currentSample,
    currentIndex,
    totalSamples,
    progress,
    showFeedback,
    referenceValues,
    submitEvaluation,
    next,
    submitting,
    loading
  };
}
